import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IncompleteProfile, JobBoxTab, JobDetailStep, State } from './types';
import {
    getJobDetailThunk,
    getAvatarPhotoThunk,
    getProfileDataThunk,
    getMatchedJobsThunk,
    getExamsHistoryThunk,
    getUserAttributesThunk,
    sendSupportMessageThunk,
    setSettingsThunk,
    updateProfileDataThunk,
    updatePersonalInformationThunk,
    getFacePhotoThunk,
} from './thunks';

const initialState: State = {
    sideBarExpanded: false,
    jobDetail: [],
    matchedJobs: [],
    examsHistory: [],
    avatarPhoto: null,
    facePhoto: null,
    profileData: null,
    sideBarShow: false,
    userAttributes: null,
    fetchingProfile: false,
    updatingSettings: false,
    fetchingJobDetail: false,
    updatingProfileData: false,
    loadingMatchedJobs: false,
    addNewCardModalShow: false,
    jobDetailStep: 'JobDetail',
    fetchingExamsHistory: false,
    sendingSupportMessage: false,
    incompleteProfileType: 'None',
    incompleteProfileModal: false,
    jobBoxTab: 'RecruitingInterview',
    uploadAvatarModal: false,
};

export const workspaceSlice = createSlice({
    name: 'workspace',
    initialState,
    reducers: {
        setSideBarShow: (state, { payload }) => {
            state.sideBarShow = payload;
        },
        setSideBarExpanded: (state, action: PayloadAction<boolean>) => {
            state.sideBarExpanded = action.payload;
        },
        setAddNewCardModalShow: (state, action) => {
            state.addNewCardModalShow = action.payload;
        },
        setJobDetail: (state, action) => {
            state.jobDetail = action.payload;
        },
        setJobDetailStep: (state, action: PayloadAction<JobDetailStep>) => {
            state.jobDetailStep = action.payload;
        },
        setIncompleteProfileModal: (state, action: PayloadAction<boolean>) => {
            state.incompleteProfileModal = action.payload;
        },
        setIncompleteProfileType: (state, action: PayloadAction<IncompleteProfile>) => {
            state.incompleteProfileType = action.payload;
        },
        setJobBoxTab: (state, action: PayloadAction<JobBoxTab>) => {
            state.jobBoxTab = action.payload;
        },
        setUploadAvatarModal: (state, action: PayloadAction<boolean>) => {
            state.uploadAvatarModal = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getAvatarPhotoThunk.fulfilled, (state, action) => {
                state.avatarPhoto = action.payload;
            })
            .addCase(getFacePhotoThunk.fulfilled, (state, action) => {
                state.facePhoto = action.payload;
            })
            .addCase(getUserAttributesThunk.fulfilled, (state, action) => {
                state.userAttributes = action.payload;
            })
            .addCase(getMatchedJobsThunk.pending, (state) => {
                state.loadingMatchedJobs = true;
            })
            .addCase(getMatchedJobsThunk.fulfilled, (state, action) => {
                state.loadingMatchedJobs = false;
                state.matchedJobs = action.payload;
            })
            .addCase(getMatchedJobsThunk.rejected, (state) => {
                state.loadingMatchedJobs = false;
            })
            .addCase(getProfileDataThunk.pending, (state) => {
                state.fetchingProfile = true;
            })
            .addCase(getProfileDataThunk.rejected, (state) => {
                state.fetchingProfile = false;
            })
            .addCase(getProfileDataThunk.fulfilled, (state, action) => {
                state.profileData = action.payload;
                state.fetchingProfile = false;
            })
            .addCase(getJobDetailThunk.pending, (state) => {
                state.fetchingJobDetail = true;
            })
            .addCase(getJobDetailThunk.fulfilled, (state, action) => {
                state.fetchingJobDetail = false;
                state.jobDetail = action.payload;
            })
            .addCase(getJobDetailThunk.rejected, (state) => {
                state.fetchingJobDetail = false;
            })
            .addCase(getExamsHistoryThunk.pending, (state) => {
                state.fetchingExamsHistory = true;
            })
            .addCase(getExamsHistoryThunk.fulfilled, (state, action) => {
                state.fetchingExamsHistory = false;
                state.examsHistory = action.payload;
            })
            .addCase(getExamsHistoryThunk.rejected, (state) => {
                state.fetchingExamsHistory = false;
            })
            .addCase(sendSupportMessageThunk.pending, (state) => {
                state.sendingSupportMessage = true;
            })
            .addCase(sendSupportMessageThunk.fulfilled, (state) => {
                state.sendingSupportMessage = false;
            })
            .addCase(sendSupportMessageThunk.rejected, (state) => {
                state.sendingSupportMessage = false;
            })
            .addCase(setSettingsThunk.rejected, (state) => {
                state.updatingSettings = false;
            })
            .addCase(setSettingsThunk.pending, (state) => {
                state.updatingSettings = true;
            })
            .addCase(setSettingsThunk.fulfilled, (state) => {
                state.updatingSettings = false;
            })
            .addCase(updateProfileDataThunk.rejected, (state) => {
                state.updatingProfileData = false;
            })
            .addCase(updateProfileDataThunk.pending, (state) => {
                state.updatingProfileData = true;
            })
            .addCase(updateProfileDataThunk.fulfilled, (state) => {
                state.updatingProfileData = false;
            })
            .addCase(updatePersonalInformationThunk.rejected, (state) => {
                state.updatingProfileData = false;
            })
            .addCase(updatePersonalInformationThunk.pending, (state) => {
                state.updatingProfileData = true;
            })
            .addCase(updatePersonalInformationThunk.fulfilled, (state) => {
                state.updatingProfileData = false;
            })
    }
})

export const {
    setJobBoxTab,
    setJobDetail,
    setSideBarShow,
    setJobDetailStep,
    setSideBarExpanded,
    setUploadAvatarModal,
    setAddNewCardModalShow,
    setIncompleteProfileType,
    setIncompleteProfileModal,
} = workspaceSlice.actions;
